// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Spinner, Textarea, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { AttachRegular, MicRegular, SendRegular } from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Constants } from '../../Constants';
import { AbtisConfig } from '../../abtis-config';
import { COPY } from '../../assets/strings';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { useFile } from '../../libs/hooks';
import { GetResponseOptions } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { editConversationInput, updateBotResponseStatus } from '../../redux/features/conversations/conversationsSlice';
import { abtisAccentColorValueString, abtisAccentHoverColorValueString } from '../../styles';
import { Alerts } from '../shared/Alerts';
import { getErrorDetails } from '../utils/TextUtils';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { ChatStatus } from './ChatStatus';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    root: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, '40px'),
    },
    typingIndicator: {
        maxHeight: '28px',
    },
    content: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        '& span:focus-within:active::after, span::after': {
            borderBottomColor: abtisAccentColorValueString,
        },
    },
    input: {
        width: '100%',
    },
    textarea: {
        maxHeight: '80px',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    chatContainer: {
        maxWidth: '900px',
        width: '100%',
    },
    wrapper: {
        '> span > svg > circle:first-child': {
            stroke: abtisAccentHoverColorValueString,
        },
        '> span > svg > circle:nth-child(2) ': {
            stroke: abtisAccentColorValueString,
        },
    },
});

interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<void>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ isDraggingOver, onDragLeave, onSubmit }) => {
    const backendUri = useSelector((state: RootState) => state.config.configValues.backendUri);

    const { instance, inProgress } = useMsal();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const fileHandler = useFile();

    const [value, setValue] = useState('');
    const [recognizer, setRecognizer] = useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = useState(false);
    const { importingDocuments } = conversations[selectedId];

    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    React.useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
    }, [selectedId]);

    const classes = useClasses();

    const teamsUserCredential = useSelector((state: RootState) => state.teams.teamsValues.teamsUserCredential);
    const inTeams = useSelector((state: RootState) => state.teams.teamsValues.inTeams);

    useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService(backendUri);
            let response;
            if (!inTeams) {
                response = await speechService.getSpeechTokenAsync(
                    await AuthHelper.getSKaaSAccessToken(instance, inProgress),
                );
            }
            if (inTeams) {
                let accessToken = '';

                const token = await teamsUserCredential?.getToken(Constants.msal.semanticKernelScopes);
                if (token) {
                    accessToken = token.token;
                }
                response = await speechService.getSpeechTokenAsync(accessToken);
            }
            if (response && response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }
        initSpeechRecognizer().catch((e) => {
            const errorDetails = getErrorDetails(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress, backendUri, inTeams, teamsUserCredential]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.disabled ? COPY.CHAT_DELETED_MESSAGE() : chatState.input);
    }, [conversations, selectedId]);

    const handleSpeech = () => {
        setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        setValue('');
        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));
        onSubmit({ value, messageType, chatId: selectedId }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            log(message);
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);
        void fileHandler.handleImport(selectedId, documentFileRef, undefined, e.dataTransfer.files);
    };

    return (
        <div className={classes.root}>
            <div className={classes.chatContainer}>
                <div className={classes.typingIndicator}>
                    <ChatStatus />
                </div>
                <Alerts />
                <div className={classes.content}>
                    <Textarea
                        title="Chat input"
                        aria-label="Chat input field. Click enter to submit input."
                        ref={textAreaRef}
                        id="chat-input"
                        resize="vertical"
                        disabled={conversations[selectedId].disabled}
                        textarea={{
                            className: isDraggingOver
                                ? mergeClasses(classes.dragAndDrop, classes.textarea)
                                : classes.textarea,
                        }}
                        className={classes.input}
                        value={isDraggingOver ? 'Drop your files here' : value}
                        onDrop={handleDrop}
                        onFocus={() => {
                            // update the locally stored value to the current value
                            const chatInput = document.getElementById('chat-input');
                            if (chatInput) {
                                setValue((chatInput as HTMLTextAreaElement).value);
                            }
                            // User is considered typing if the input is in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo.id,
                                        chatId: selectedId,
                                        isTyping: true,
                                    }),
                                );
                            }
                        }}
                        onChange={(_event, data) => {
                            if (isDraggingOver) {
                                return;
                            }

                            setValue(data.value);
                            dispatch(editConversationInput({ id: selectedId, newInput: data.value }));
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleSubmit(value);
                            }
                        }}
                        onBlur={() => {
                            // User is considered not typing if the input is not  in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo.id,
                                        chatId: selectedId,
                                        isTyping: false,
                                    }),
                                );
                            }
                        }}
                    />
                </div>
                <div className={classes.controls}>
                    <div className={classes.functional}>
                        {AbtisConfig.featureFlags.showDocumentUploadInChatButton && (
                            <>
                                {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                                <input
                                    type="file"
                                    ref={documentFileRef}
                                    style={{ display: 'none' }}
                                    accept={Constants.app.importTypes}
                                    multiple={true}
                                    onChange={() => {
                                        void fileHandler.handleImport(selectedId, documentFileRef);
                                    }}
                                />
                                <Button
                                    disabled={
                                        conversations[selectedId].disabled ||
                                        (importingDocuments && importingDocuments.length > 0)
                                    }
                                    appearance="transparent"
                                    icon={<AttachRegular />}
                                    onClick={() => documentFileRef.current?.click()}
                                    title="Attach file"
                                    aria-label="Attach file button"
                                />
                                {importingDocuments && importingDocuments.length > 0 && <Spinner size="tiny" />}
                            </>
                        )}
                    </div>
                    <div className={classes.essentials}>
                        {recognizer && (
                            <Button
                                appearance="transparent"
                                disabled={conversations[selectedId].disabled || isListening}
                                icon={<MicRegular />}
                                onClick={handleSpeech}
                            />
                        )}
                        <Button
                            title="Submit"
                            aria-label="Submit message"
                            appearance="transparent"
                            icon={<SendRegular />}
                            onClick={() => {
                                handleSubmit(value);
                            }}
                            disabled={conversations[selectedId].disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
